/**
 * Club page
 */
import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import ClubBanner from "./../components/clubs/ClubBanner";
import Container from "./../components/core/Container";
import Overview from "./../components/clubs/ClubOverview";
import Roster from "../components/clubs/ClubRoster";
import ClubSchedule from "../components/clubs/ClubSchedule";
import ClubStandings from "../components/clubs/ClubStandings";
import ClubNews from "../components/clubs/ClubNews";
import {useQuery} from "@tanstack/react-query";
import {Helmet} from "react-helmet-async";
import ClubSidebar from "../components/clubs/ClubSidebar";
import Loading from "../components/core/Loading";
import PageSidebarContainer from "../components/core/PageSidebarContainer";
import ContentContainer from "../components/core/ContentContainer";
import ClubStats from "../components/clubs/ClubStats";

const processClubData = (jsonData) => {
  let processedData = {};

  processedData.id = jsonData.id;
  processedData.stub = jsonData.stub;
  processedData.name = jsonData.name;
  processedData.short = jsonData.short;
  processedData.full_name = jsonData.full_name;
  processedData.primary_color = "#" + jsonData.primary_color;
  processedData.secondary_color = "#" + jsonData.secondary_color;
  processedData.img = jsonData.logo;
  processedData.teams = jsonData.teams;
  processedData.home = jsonData.home;
  processedData.founded = jsonData.founded;
  processedData.members = jsonData.members;
  processedData.roster_seasons = jsonData.roster_seasons;
  processedData.games_seasons = jsonData.games_seasons;
  processedData.divisions_seasons = jsonData.divisions_seasons;
  processedData.fb = jsonData.fb;
  processedData.tw = jsonData.tw;
  processedData.ig = jsonData.ig;
  processedData.yt = jsonData.yt;
  processedData.institutions = jsonData.institutions;
  processedData.rink_address = jsonData.rink_address;
  processedData.club_address = jsonData.club_address;
  processedData.training = jsonData.training;
  processedData.detail = jsonData.detail;
  processedData.committee = jsonData.committee;
  processedData.news = jsonData.news;
  processedData.web = jsonData.web;

  return processedData;
};

const Club = () => {
  const { clubId, tab } = useParams(); // Get clubId and tab from URL params
  const navigate = useNavigate(); // Initialize useNavigate hook
  const [activeTab, setActiveTab] = useState(tab || "about"); // Set activeTab from URL or default to 'about'
  const [tabContents, setTabContents] = useState(null); // Initialize tabContents as null
  const [rosterData, setRosterData] = useState(null); // State to store roster data

  // Fetch the club data using react-query
  const { isLoading, error, data, refetch } = useQuery({
    staleTime: Infinity,
    queryKey: ["clubs", clubId],
    queryFn: () =>
      fetch(`${process.env.REACT_APP_BUIHA_API_URL}/club/${clubId}/`)
        .then((res) => res.json())
        .then((json) => {
          const clubData = processClubData(json);

          if (clubId != clubData.stub) {
            if (tab != undefined) {
              navigate(`/club/${clubData.stub}/${tab}`, { replace: true });
            } else {
              navigate(`/club/${clubData.stub}/`, { replace: true });
            }
          }

          setTabContents({
            about: <Overview data={clubData} />,
            roster: (
                <Roster
                    clubData={clubData}
                    clubId={clubId}
                    rosterData={rosterData}
                    setRosterData={setRosterData}
                />
            ),
            schedule: <ClubSchedule clubData={clubData} />,
            standings: <ClubStandings clubData={clubData} />,
            news: <ClubNews clubData={clubData} />,
            stats: <ClubStats clubData={clubData} />,
          });

          return clubData;
        })
        .catch(error => {
          navigate('/clubs')
        })
  });

  // Handle tab click and update URL when user clicks on a tab
  const handleTabClick = (tab) => {
    setActiveTab(tab); // Update the active tab state
    navigate(`/club/${clubId}/${tab}`); // Update the URL with the new tab
  };

  // Sync the active tab with the URL param when URL changes (back/forward navigation)
  useEffect(() => {
    if (tab) {
      setActiveTab(tab); // Update the activeTab state based on URL param
    } else {
      setActiveTab("about"); // Fallback to 'overview' if no tab is provided
    }
  }, [tab]);

  // // Populate tab contents after data is successfully fetched
  // useEffect(() => {
  //   if (data && clubId !== activeClub) {
  //     setActiveClub(clubId);
  //   }
  // }, [data, clubId, rosterData]);

  // Re-fetch data if gameId changes
  useEffect(() => {
    refetch();
  }, [clubId, refetch]);

  // Ensure loading and error states are handled
  if (isLoading)
    return (
        <>
          <ClubBanner
              gbs={false}
              handleTabClick={handleTabClick} // Pass handleTabClick to ClubBanner for tab switching
              name={""}
              col1={"#111111"}
              col2={"#222222"}
          />
          <Container>
            <Loading/>
          </Container>
        </>
    );
    
  if (error) return "An error has occurred: " + error.message;

  const renderTabContent = () => {
    return tabContents && tabContents[activeTab]
      ? tabContents[activeTab]
      : null;
  };

  const gradientStyle = {
    background: `linear-gradient(90deg, ${data.primary_color}, ${data.secondary_color})`,
  };

  return (
    <>
      {data && (
        <>
          <Helmet>
            <title>{data.name} | BUIHA Club Page</title>
            <meta
              name="description"
              content={`Club page for the ${data.name} Ice Hockey Club in the BUIHA including Overview, Roster, Schedule, Standings, News`}
            />
            <link rel="canonical" href={`https://buiha.org/club/${clubId}`} />
            {/* Add favicon */}
            <link rel="icon" type="image/png" href={data.img.md} />
            

          </Helmet>

          <ClubBanner
            gbs={false}
            activeTab={activeTab}
            handleTabClick={handleTabClick} // Pass handleTabClick to ClubBanner for tab switching
            name={data.name}
            col1={data.primary_color}
            col2={data.secondary_color}
            img={data.img}
            home={data.home}
            founded={data.founded}
            members={data.members}
            fb={data.fb}
            tw={data.tw}
            ig={data.ig}
            yt={data.yt}
            web={data.web}
            news={data.news}
          />

          <Container>
            <PageSidebarContainer
              children={
                <>
                  <ContentContainer
                    children={<>{renderTabContent()}</>}
                    extraClasses= {"mt-[16px] min-h-[50vh]"}
                  />
                  <ClubSidebar data={data} gradientStyle={gradientStyle} />
                </>
              }
            />
          </Container>
        </>
      )}
    </>
  );
};

export default Club;
